// src/pages/KycList.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Toolbar,
  InputBase,
  ButtonGroup,
  TablePagination,
  Avatar,
  Stack,
  useTheme,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";

import { getKycList, approveKyc, rejectKyc } from "../services/api";

/* ---------- helpers ---------- */
const safe = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);
const initialOf = (name) => (name ? String(name).trim().charAt(0).toUpperCase() : "U");
const statusColor = (s) => {
  const st = (s || "").toString().toLowerCase();
  if (st === "approved") return "success";
  if (st === "rejected") return "error";
  return "warning";
};

// emoji flag from ISO-2 code
const flagOf = (code) => {
  const c = String(code || "").trim().toUpperCase();
  if (c.length !== 2) return "🌍";
  return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 - 65 + ch.charCodeAt(0)));
};

const countryName = (code) => {
  const c = String(code || "").trim().toUpperCase();
  const map = {
    LK: "Sri Lanka",
    IN: "India",
    AU: "Australia",
    CA: "Canada",
    UK: "United Kingdom",
    GB: "United Kingdom",
    US: "United States",
    SG: "Singapore",
    NZ: "New Zealand",
    OTHER: "Other",
  };
  return map[c] || c || "—";
};

const parseReviewNotes = (notes) => {
  try {
    return typeof notes === "string" ? JSON.parse(notes) : notes || {};
  } catch {
    return {};
  }
};

/* ---------- component ---------- */
export default function KycList() {
  const theme = useTheme();

  const [kycs, setKycs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // UI state
  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | pending | approved | rejected
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // dialogs & preview
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);

  // confirmAction -> { id, action, name, notes }
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await getKycList();
      const data = res?.data ?? res;
      const list = Array.isArray(data) ? data : data?.data ?? [];
      setKycs(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Failed to load KYC list", e);
      setErrorMsg(e?.response?.data?.error || e?.message || "Failed to load KYC list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return kycs.filter((k) => {
      if (filterStatus !== "all") {
        const s = (k.status || "").toString().toLowerCase();
        if (filterStatus === "pending" && s !== "pending") return false;
        if (filterStatus === "approved" && s !== "approved") return false;
        if (filterStatus === "rejected" && s !== "rejected") return false;
      }
      if (!q) return true;

      const name = (k.user?.name || "").toString().toLowerCase();
      const email = (k.user?.email || "").toString().toLowerCase();
      const id = (k.user?.rockstarId || "").toString().toLowerCase();

      const countryCode = (k.country || "").toString().toLowerCase();
      const countryFull = countryName(k.country).toLowerCase();
      const jurisdiction = (parseReviewNotes(k.reviewNotes).kycJurisdiction || "")
        .toString()
        .toLowerCase();

      return (
        name.includes(q) ||
        email.includes(q) ||
        id.includes(q) ||
        countryCode.includes(q) ||
        countryFull.includes(q) ||
        jurisdiction.includes(q)
      );
    });
  }, [kycs, query, filterStatus]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // open confirm with an editable notes field
  const openConfirm = (kyc, action) => {
    setConfirmAction({
      id: kyc.id || kyc._id,
      action,
      name: kyc.user?.name || "User",
      notes: "",
    });
  };
  const closeConfirm = () => setConfirmAction(null);

  const handleConfirmNotesChange = (e) => {
    setConfirmAction((s) => (s ? { ...s, notes: e.target.value } : s));
  };

  const performAction = async () => {
    if (!confirmAction) return;
    setActionBusy(true);
    try {
      const { id, action, notes } = confirmAction;
      if (action === "approve") {
        await approveKyc(id, { notes });
      } else {
        await rejectKyc(id, { notes });
      }
      await load();
    } catch (e) {
      console.error("Action failed", e);
      setErrorMsg(e?.response?.data?.error || e?.message || "Action failed");
    } finally {
      setActionBusy(false);
      closeConfirm();
    }
  };

  const openViewer = (k) => setSelectedKyc(k);
  const closeViewer = () => setSelectedKyc(null);

  const visible = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container
      maxWidth="lg"
      sx={{
        pt: { xs: 10, sm: 12 }, // safe space under fixed header
        pb: 6,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200 }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            KYC Requests
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              background: "linear-gradient(180deg, rgba(255,255,255,0.01), rgba(255,255,255,0.005))",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Toolbar sx={{ gap: 2, display: "flex", flexWrap: "wrap" }}>
              <Paper
                component="form"
                onSubmit={(e) => e.preventDefault()}
                sx={{
                  p: "6px 10px",
                  display: "flex",
                  alignItems: "center",
                  width: { xs: "100%", sm: 420 },
                  borderRadius: 2,
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <SearchIcon color="action" />
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search name, email, rockstar id, country, jurisdiction..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  inputProps={{ "aria-label": "search" }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setQuery("");
                  }}
                  aria-label="clear"
                >
                  ✕
                </IconButton>
              </Paper>

              <ButtonGroup variant="outlined" size="small" aria-label="status filters" sx={{ ml: 1 }}>
                <Button color={filterStatus === "all" ? "primary" : "inherit"} onClick={() => setFilterStatus("all")}>
                  All
                </Button>
                <Button color={filterStatus === "pending" ? "warning" : "inherit"} onClick={() => setFilterStatus("pending")}>
                  Pending
                </Button>
                <Button color={filterStatus === "approved" ? "success" : "inherit"} onClick={() => setFilterStatus("approved")}>
                  Approved
                </Button>
                <Button color={filterStatus === "rejected" ? "error" : "inherit"} onClick={() => setFilterStatus("rejected")}>
                  Rejected
                </Button>
              </ButtonGroup>

              <Box sx={{ flexGrow: 1 }} />

              <Tooltip title="Refresh">
                <IconButton onClick={load} size="large">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            {loading ? (
              <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {errorMsg && (
                  <Box sx={{ p: 2 }}>
                    <Typography color="error">{errorMsg}</Typography>
                  </Box>
                )}

                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{ background: "rgba(255,255,255,0.02)" }}>
                        <TableCell sx={{ width: 72 }}>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Rockstar ID</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell sx={{ width: 140 }}>Status</TableCell>
                        <TableCell align="right" sx={{ width: 160 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visible.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                            <Typography color="text.secondary">No KYC requests</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        visible.map((k, i) => {
                          const id = k.id ?? k._id ?? `r-${i}`;
                          const index = page * rowsPerPage + i + 1;
                          const notes = parseReviewNotes(k.reviewNotes);
                          const j = notes.kycJurisdiction;

                          return (
                            <TableRow
                              key={id}
                              hover
                              sx={{
                                "&:nth-of-type(odd)": { background: "rgba(255,255,255,0.01)" },
                                "&:hover": { background: "rgba(255,255,255,0.02)" },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {index}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
                                    {initialOf(k.user?.name)}
                                  </Avatar>
                                  <Box>
                                    <Typography sx={{ fontWeight: 600 }}>{safe(k.user?.name)}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {safe(k.user?.phone, "")}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2">{safe(k.user?.email)}</Typography>
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2">{safe(k.user?.rockstarId)}</Typography>
                              </TableCell>

                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <span style={{ fontSize: 18 }}>{flagOf(k.country)}</span>
                                  <Typography variant="body2">{countryName(k.country)}</Typography>
                                  {j && (
                                    <Chip size="small" label={j} variant="outlined" sx={{ ml: 0.5 }} />
                                  )}
                                </Stack>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={safe(k.status || "Pending")}
                                  color={statusColor(k.status)}
                                  size="small"
                                  sx={{ fontWeight: 700, letterSpacing: 0.3 }}
                                />
                              </TableCell>

                              <TableCell align="right">
                                <Tooltip title="Approve">
                                  <span>
                                    <IconButton
                                      color="success"
                                      size="small"
                                      onClick={() => openConfirm(k, "approve")}
                                      disabled={actionBusy}
                                    >
                                      <CheckCircleOutlineIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip title="Reject">
                                  <span>
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => openConfirm(k, "reject")}
                                      disabled={actionBusy}
                                    >
                                      <CancelOutlinedIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip title="View documents">
                                  <IconButton size="small" onClick={() => openViewer(k)}>
                                    <VisibilityOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filtered.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  showFirstButton
                  showLastButton
                />
              </>
            )}
          </Paper>

          {/* Documents dialog */}
          <Dialog open={Boolean(selectedKyc)} onClose={closeViewer} maxWidth="lg" fullWidth>
            <DialogTitle>
              Documents — {selectedKyc?.user?.name ?? "User"}{" "}
              <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: 14 }}>
                {flagOf(selectedKyc?.country)} {countryName(selectedKyc?.country)}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {selectedKyc?.documents?.length ? (
                <Grid container spacing={2}>
                  {selectedKyc.documents.map((doc) => (
                    <Grid key={doc.id || doc._id || doc.docType} item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                        {doc.docType}
                      </Typography>

                      {/\.(jpg|jpeg|png|gif|webp)$/i.test(doc.fileUrl || "") ? (
                        <Box sx={{ position: "relative" }}>
                          <img
                            src={doc.fileUrl}
                            alt={doc.docType}
                            style={{
                              width: "100%",
                              borderRadius: 8,
                              border: `1px solid ${theme.palette.divider}`,
                              cursor: "zoom-in",
                            }}
                            onClick={() => setPreviewImg(doc.fileUrl)}
                          />

                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button size="small" variant="outlined" href={doc.fileUrl} target="_blank">
                              Open
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<OpenInFullIcon />}
                              onClick={() => setPreviewImg(doc.fileUrl)}
                            >
                              Preview
                            </Button>
                          </Stack>
                        </Box>
                      ) : (
                        <Box>
                          <Button href={doc.fileUrl} target="_blank" variant="outlined" size="small">
                            {doc.originalName || "Download"}
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography>No documents available</Typography>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={closeViewer}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* image preview lightbox */}
          <Dialog open={Boolean(previewImg)} onClose={() => setPreviewImg(null)} maxWidth="xl">
            <Box sx={{ p: 2 }}>
              <img src={previewImg || ""} alt="preview" style={{ maxWidth: "90vw", maxHeight: "80vh", display: "block" }} />
            </Box>
          </Dialog>

          {/* Approve/reject confirmation with notes */}
          <Dialog open={Boolean(confirmAction)} onClose={closeConfirm} maxWidth="sm" fullWidth>
            <DialogTitle>
              {confirmAction?.action === "approve" ? "Approve KYC" : "Reject KYC"}
            </DialogTitle>

            <DialogContent dividers>
              <Typography sx={{ mb: 2 }}>
                Are you sure you want to <strong>{confirmAction?.action}</strong> the KYC request for{" "}
                <strong>{confirmAction?.name}</strong>?
              </Typography>

              <TextField
                label="Admin notes (optional)"
                placeholder="Add a short note for the user / audit logs"
                value={confirmAction?.notes ?? ""}
                onChange={handleConfirmNotesChange}
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={closeConfirm} disabled={actionBusy}>
                Cancel
              </Button>

              <Button
                variant="contained"
                color={confirmAction?.action === "approve" ? "success" : "error"}
                onClick={performAction}
                disabled={actionBusy}
              >
                {actionBusy ? "Please wait..." : confirmAction?.action === "approve" ? "Approve" : "Reject"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Container>
  );
}
